import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Wordmark } from "./Wordmark";
import { IMAGES } from "../../lib/content";
import { scrollToSection } from "../../lib/scroll";

const LINKS = [
  { label: "The Estate", href: "#manifesto" },
  { label: "Community", href: "#community" },
  { label: "Residences", href: "#residences" },
  { label: "Amenities", href: "#amenities" },
  { label: "Location", href: "#location" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    // Wait a tick so the mobile menu collapse doesn't shift the target's position.
    setTimeout(() => scrollToSection(href), 60);
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "glass border-b border-[#d4af37]/15" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1400px] px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 md:gap-4"
        >
          <img
            src={IMAGES.diamondLogo}
            alt="Diamond India Builders — Conscious Luxury"
            className="w-16 h-16 md:w-[72px] md:h-[72px] object-contain drop-shadow-[0_2px_12px_rgba(0,0,0,0.55)]"
          />
          <span className="h-8 w-px bg-[#d4af37]/25 hidden sm:block" />
          <Wordmark size="text-xl md:text-2xl" className="hidden sm:inline" />
        </button>

        <div className="hidden md:flex items-center gap-2 glass rounded-full border border-[#d4af37]/20 px-3 py-1.5">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.href.replace("#", "")}`}
              onClick={() => go(l.href)}
              className="rounded-full px-4 py-2 text-[12px] tracking-[0.16em] uppercase text-[#eee9dc] hover:text-[#0a0a0a] hover:bg-[#d4af37] transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-enquire"
            onClick={() => go("#enquire")}
            className="btn-gold rounded-full px-6 py-2.5 text-[12px] tracking-[0.16em] uppercase font-semibold ml-1"
          >
            Enquire
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-[#d4af37]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden glass border-t border-[#d4af37]/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-nav-${l.href.replace("#", "")}`}
                  onClick={() => go(l.href)}
                  className="text-left text-sm tracking-[0.18em] uppercase text-[#c9c5ba]"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-nav-enquire"
                onClick={() => go("#enquire")}
                className="btn-gold rounded-full px-6 py-3 text-xs tracking-[0.16em] uppercase font-semibold"
              >
                Enquire
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
